import { action, makeObservable, observable } from 'mobx';
import { RoundStatus } from '@pascal/pascal-core';
import { betStore } from '@pascal/pascal-core/shared/core/store';

import { delay } from '../helpers';
import { multiplierDelay } from './gameStore';
import { bonusIndexes, candiesButtonInitialData, WheelTypes } from '../enums';

export const waitWheelChangeAnimationInSecondsMs = 750;
export const wheelDefaultAnimationDuration = 4000;
const firstWheelAnimationDuration =
  multiplierDelay + wheelDefaultAnimationDuration + waitWheelChangeAnimationInSecondsMs;
const secondWheelAnimationDuration =
  wheelDefaultAnimationDuration + waitWheelChangeAnimationInSecondsMs;

const indexLightningWaitSecondsMs = 300;
const defaultZoomAnimDelay = 2000;
const defaultZoomAnimDuration = 4000;

class CandyDataStore {
  current = null;

  rtpType = null;

  reBetData = null;

  result = null;

  resultIndex = null;

  gameAnim = {
    animationDelay: '',
    animationDuration: '',
  };

  zoomAnim = {
    animationDelay: '',
    animationDuration: '',
  };

  currentWheelType = WheelTypes.first;

  nextWheelType = WheelTypes.first;

  multiplierData = WheelTypes.first;

  candiesBetData = candiesButtonInitialData;

  constructor() {
    makeObservable(this, {
      result: observable.deep,
      current: observable.deep,
      rtpType: observable,
      reBetData: observable,
      gameAnim: observable,
      zoomAnim: observable,
      resultIndex: observable.deep,
      nextWheelType: observable.deep,
      multiplierData: observable.deep,
      currentWheelType: observable.deep,
      candiesBetData: observable.deep,
      setGameAnim: action.bound,
      setZoomAnim: action.bound,
      setRtpType: action.bound,
      resetGameAnim: action.bound,
      resetZoomAnim: action.bound,
      setReBetData: action.bound,
      setGameResult: action.bound,
      setResultIndex: action.bound,
      resetReBetData: action.bound,
      setNextWheelType: action.bound,
      resetCandiesData: action.bound,
      setCandiesBetData: action.bound,
      setMultiplierData: action.bound,
      setCurrentWheelType: action.bound,
      resetCandiesGameData: action.bound,
      callDecidedAnimation: action.bound,
    });
  }

  setCurrent(current) {
    this.current = current;
  }

  setRtpType(rtpType) {
    this.rtpType = rtpType;
  }

  setGameResult(result) {
    this.result = result;
  }

  resetGameAnim() {
    this.gameAnim = {
      animationDelay: '',
      animationDuration: '',
    };
  }

  resetZoomAnim() {
    this.zoomAnim = {
      animationDelay: '',
      animationDuration: '',
    };
  }

  setGameAnim(gameAnim) {
    this.gameAnim = { ...gameAnim };
  }

  setZoomAnim(zoomAnim) {
    this.zoomAnim = { ...zoomAnim };
  }

  setCandiesBetData(candiesBetData) {
    this.candiesBetData = candiesBetData;
  }

  setResultIndex(resultIndex) {
    this.resultIndex = resultIndex;
  }

  setCurrentWheelType(currentWheelType) {
    this.currentWheelType = currentWheelType;
  }

  setNextWheelType(nextWheelType) {
    this.nextWheelType = nextWheelType;
  }

  setMultiplierData(multiplierData) {
    this.multiplierData = multiplierData;
  }

  resetCandiesData() {
    if (!betStore.repliedBetData) {
      this.candiesBetData = candiesButtonInitialData;
    }
  }

  setReBetData(reBetData) {
    this.reBetData = reBetData;
  }

  callDecidedAnimation({ started, betsClosedDuration, second, status }) {
    const time = (second + betsClosedDuration) * 1000;
    let updatedDuration;
    let updatedStarted;
    let delayTime;
    if (time < 0 && second && time) {
      let passedTime;
      switch (this.currentWheelType) {
        case WheelTypes.first:
          passedTime = -time - multiplierDelay;
          updatedStarted =
            passedTime < 0
              ? multiplierDelay - started
              : passedTime > wheelDefaultAnimationDuration
              ? -wheelDefaultAnimationDuration
              : -passedTime;
          updatedDuration = wheelDefaultAnimationDuration;
          break;
        case WheelTypes.second:
          passedTime = -time - firstWheelAnimationDuration;
          updatedStarted =
            passedTime > wheelDefaultAnimationDuration
              ? -wheelDefaultAnimationDuration
              : wheelDefaultAnimationDuration - passedTime;
          updatedDuration = wheelDefaultAnimationDuration;

          break;
        default:
          passedTime = -time - firstWheelAnimationDuration - secondWheelAnimationDuration;
          updatedStarted =
            passedTime > wheelDefaultAnimationDuration
              ? -wheelDefaultAnimationDuration
              : wheelDefaultAnimationDuration - passedTime;
          updatedDuration = wheelDefaultAnimationDuration;
      }
    } else {
      updatedStarted = 0;
      updatedDuration = wheelDefaultAnimationDuration; // duration;
    }
    delayTime =
      updatedStarted < 0
        ? wheelDefaultAnimationDuration + updatedStarted
        : wheelDefaultAnimationDuration;
    this.setGameAnim({
      animationDelay: `${updatedStarted}ms`,
      animationDuration: `${updatedDuration}ms`,
    });
    setTimeout(() => {
      if (![RoundStatus.OnFinish, RoundStatus.Finished].includes(status) && updatedStarted) {
        this.setZoomAnim({
          animationDelay: `${updatedStarted + defaultZoomAnimDelay}ms`,
          animationDuration: `${defaultZoomAnimDuration}ms`,
        });
      }
    }, updatedDuration - 120);
    if (delayTime) {
      delay(delayTime - indexLightningWaitSecondsMs).then(() => {
        if (this?.result && this?.result?.wheelResult) {
          const {
            wheelResult: { index },
          } = this?.result;
          this.setResultIndex(index);
        }
      });
    }
    delay(indexLightningWaitSecondsMs).then(() => {
      this.setGameAnim(null);
      if (this?.result && this?.result?.wheelResult) {
        const {
          wheelResult: { index },
        } = this?.result;
        if (bonusIndexes[this?.currentWheelType]?.includes(index)) {
          this.setCurrentWheelType(this.currentWheelType + 1);
        }
      }
    });
  }

  resetReBetData() {
    this.reBetData = null;
  }

  resetCandiesGameData() {
    this.setGameAnim(null);
    this.resetZoomAnim();
    this.setGameResult(null);
    this.resetCandiesData();
    this.setMultiplierData(null);
    this.setResultIndex(null);
  }
}

export const candyDataStore = new CandyDataStore();
